/**
 * mr-dedication.js — Sprint 6 Phase 3
 *
 * Purpose: watch the Infinite Options (IO) beneficiary-name field rendered
 * inside the purchase column and, read-only, reveal the mr-c-dedication
 * confirmation line the moment a beneficiary name has been typed — no page
 * reload, no IO state mutation.
 *
 * REAL IO FIELD NAME (pulled fresh from the live app config, re-verified
 * 2026-07-14 across Gift Qur'an, Waqf Wheelchair and Waqf Chair):
 *   - input[type=text][name="properties[Beneficiary Name]"] — the name
 *     itself. This is the only field this script depends on.
 * If Operations ever renames this field in the IO app config, this
 * selector must be re-verified against the live config, same as
 * mr-c-beneficiary-step's own byte-level property-key testing.
 *
 * Sprint 7 Refinement, Phase 1, Stage B (2026-07-14) — two confirmed
 * root causes fixed: a DOM-scope bug (fields live outside
 * .mr-c-beneficiary-step, as a sibling inside the shared buy column) and
 * a timing bug (IO mounts its fields asynchronously, after this script's
 * own first pass). Both fixes are retained below (widened buy-column
 * lookup, MutationObserver + give-up timer).
 *
 * Sprint 7 Refinement, "Three Remaining Items" (2026-07-14), Item 1 — a
 * THIRD, more serious root cause found and fixed here, after Fable's
 * Creative Direction review reported being unable to reproduce the
 * Acknowledgment at all. WRONG REVEAL CONDITION: the previous version
 * required BOTH an unrelated/nonexistent checkbox AND the name field to
 * be non-empty. Live testing with real keyboard input proved this wrong
 * on every product tested — fixed by making the reveal condition the name
 * field alone (`hasName = trimmed.length > 0`). All checkbox wiring
 * (`findNameCheckbox`, etc.) has been removed — this script no longer
 * reads any checkbox, anywhere. Matches MR-Commitment-Ceremony.md
 * Movement II, Law 5 exactly: "The Acknowledgment always answers the
 * name" — unconditionally.
 *
 * Sprint 7 Refinement, "Three Remaining Items" (2026-07-14), Item 2 (Host
 * Voice) — extended, not rewritten, to also populate a second, purely
 * decorative element: .mr-c-silence-two (Movement VI, "Silence Two, the
 * Breath" — a small echo of the name just before the button). This reuses
 * the exact same read-only name-state this script already tracks for the
 * Acknowledgment — no new field reads, no new IO interaction. The element
 * is optional (`document.querySelector`, may be null on older cached
 * markup) and, like the Acknowledgment itself, is marked aria-hidden in
 * the markup so screen readers are not told the same name twice; this
 * script does not touch that attribute, only textContent.
 *
 * 2026-07-18 — Silence Two redesign (founder verdict: old echo's mono
 * type + dead gap). The echo now lives inside an optional wrapper
 * ([data-mr-silence-two-wrap], gated by the buy_buttons block's
 * `show_name_echo` checkbox) with an ornament and a dedicated name span
 * ([data-mr-silence-two-name]). This script now: writes the trimmed name
 * into that span when present (falling back to the old whole-element
 * 'for X' write on older cached markup), and toggles the wrapper's
 * `mr-c-silence-two-wrap--on` class so CSS (stage-m §8) can collapse the
 * whole moment — spacing included — when no name is typed, and play the
 * reveal motion when one is. Still write-only to owned elements; still
 * no PII anywhere but visible textContent.
 *
 * 2026-07-18 (2) — THE DU'A MERGE (founder-approved). The typed name now
 * lives INSIDE the reassurance (du'a) line itself: the section wraps the
 * literal phrase "the one you chose" in [data-mr-duaa-name]. This script
 * swaps that span's text to the trimmed beneficiary name when one is
 * typed (adding `mr-c-duaa-name--named` — the jewel styling, stage-m §9)
 * and back to the original phrase when cleared. The swap crossfades on
 * the named/unnamed EDGE only (150ms out / 250ms in, house ease — timed
 * here, eased by the CSS transition); keystrokes within an existing name
 * update textContent in place, no flicker; prefers-reduced-motion gets an
 * instant swap. Same single input listener as everything above — no new
 * listeners, no new field reads. The retired Silence Two echo wiring is
 * kept intact for the `show_name_echo` toggle (now default-off). If the
 * founder edits the reassurance text so the phrase — and therefore the
 * span — disappears, duaaEl is null and the line is plain static text.
 * `init` now proceeds when EITHER the dedication or the du'a span exists
 * (dedication is null-guarded throughout).
 *
 * READ-ONLY OVER THE APP — HARD RULE: this script only ever *reads*
 * `.value` off IO's own name input via a passive 'input' listener. It
 * never calls `.value =`, `.checked =`, `.dispatchEvent(...)`,
 * `.click()`, `.focus()`, `.setAttribute(...)`, or any other mutating
 * method on any element inside the buy column's IO app block. It only
 * ever writes to elements inside `.mr-c-dedication`, `.mr-c-silence-two`
 * and `[data-mr-duaa-name]` (components this sprint owns outright).
 *
 * NO PII, ANYWHERE BUT THE VISIBLE UI: the beneficiary name is written
 * only to the text content of mr-c-dedication's `[data-mr-dedication-
 * name]` span, mr-c-silence-two's own text content, and the du'a line's
 * `[data-mr-duaa-name]` span. It is never console.logged, written to a
 * data-* attribute, stored in localStorage/sessionStorage, or included
 * in the pubsub payload below (payload is a boolean only).
 *
 * PUBSUB: publishes 'mr:dedication:updated' via Dawn's existing
 * subscribe/publish global convention (assets/pubsub.js) so a later
 * consumer can react without re-reading the DOM. Fires only on actual
 * state change (edge-triggered), not per keystroke. Payload carries a
 * boolean only — never the name.
 *
 * No scroll-event listeners. Progressive enhancement: if this script
 * fails to load, mr-c-dedication simply never reveals (CSS default is
 * `display:none`) and the du'a line keeps its static phrase — purchase
 * and IO's own name-collection are entirely unaffected.
 */
(function () {
  'use strict';

  var BUY_CONTAINER_SELECTOR = '.mr-s-product-commitment__buy';
  var IO_APP_BLOCK_SELECTOR = '[id$="__io_app_block"]';
  var DEDICATION_SELECTOR = '.mr-c-dedication';
  var SILENCE_TWO_SELECTOR = '.mr-c-silence-two';
  var SILENCE_WRAP_SELECTOR = '[data-mr-silence-two-wrap]';
  var SILENCE_NAME_SELECTOR = '[data-mr-silence-two-name]';
  var SILENCE_WRAP_ON_CLASS = 'mr-c-silence-two-wrap--on';
  var DUAA_NAME_SELECTOR = '[data-mr-duaa-name]';
  var DUAA_NAMED_CLASS = 'mr-c-duaa-name--named';
  var DUAA_FADE_OUT_MS = 150;
  var DUAA_FADE_IN_MS = 250;
  var NAME_INPUT_SELECTOR = 'input[type="text"][name="properties[Beneficiary Name]"]';
  var VISIBLE_CLASS = 'mr-c-dedication--visible';
  var PUBSUB_EVENT = 'mr:dedication:updated';
  var GIVE_UP_MS = 8000; // matches mr-io-loading.js's own safety-net window

  function publishSafely(eventName, data) {
    if (typeof window.publish === 'function') {
      window.publish(eventName, data);
    }
  }

  function bindFields(nameInput, dedication, silenceTwo, duaaEl) {
    var nameEl = dedication ? dedication.querySelector('[data-mr-dedication-name]') : null;
    var silenceWrap = silenceTwo ? silenceTwo.closest(SILENCE_WRAP_SELECTOR) : null;
    var silenceNameEl = silenceTwo ? silenceTwo.querySelector(SILENCE_NAME_SELECTOR) : null;
    var lastHasName = null; // edge-trigger tracker, not yet known

    // 2026-07-18 (2): du'a-line span state. The span ships holding the
    // founder's phrase ("the one you chose") — capture it as the revert
    // text so we never hardcode copy here.
    var duaaDefault = duaaEl ? duaaEl.textContent : '';
    var duaaTimer = null;
    var prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function setDuaa(text, named) {
      duaaEl.textContent = text;
      duaaEl.classList.toggle(DUAA_NAMED_CLASS, named);
    }

    function writeDuaa(trimmed) {
      if (!duaaEl) return;
      var named = trimmed.length > 0;
      var target = named ? trimmed : duaaDefault;
      var wasNamed = duaaEl.classList.contains(DUAA_NAMED_CLASS);

      if (named === wasNamed && duaaEl.textContent === target) return; // no-op

      if (prefersReducedMotion) {
        if (duaaTimer) { clearTimeout(duaaTimer); duaaTimer = null; }
        setDuaa(target, named);
        duaaEl.style.opacity = '';
        return;
      }

      if (named === wasNamed) {
        // Keystroke inside an existing name — update in place, no fade.
        setDuaa(target, named);
        return;
      }

      // Named/unnamed edge — quick crossfade: dip out, swap, breathe in.
      if (duaaTimer) clearTimeout(duaaTimer);
      duaaEl.style.transitionDuration = DUAA_FADE_OUT_MS + 'ms';
      duaaEl.style.opacity = '0';
      duaaTimer = setTimeout(function () {
        setDuaa(target, named);
        duaaEl.style.transitionDuration = DUAA_FADE_IN_MS + 'ms';
        duaaEl.style.opacity = '1';
        duaaTimer = null;
      }, DUAA_FADE_OUT_MS);
    }

    // 2026-07-18: write the echo. New markup has a dedicated name span +
    // wrapper class toggle (collapse/reveal handled by stage-m §8 CSS);
    // old cached markup falls back to the original whole-element write.
    function writeSilence(trimmed) {
      if (!silenceTwo) return;
      if (silenceNameEl) {
        silenceNameEl.textContent = trimmed;
      } else {
        silenceTwo.textContent = trimmed ? 'for ' + trimmed : '';
      }
      if (silenceWrap) {
        silenceWrap.classList.toggle(SILENCE_WRAP_ON_CLASS, trimmed.length > 0);
      }
    }

    function sync() {
      var rawValue = nameInput.value || '';
      var trimmed = rawValue.trim();
      var hasName = trimmed.length > 0;

      if (hasName) {
        if (nameEl) nameEl.textContent = trimmed;
        if (dedication) dedication.classList.add(VISIBLE_CLASS);
        writeSilence(trimmed);
        writeDuaa(trimmed);
      } else {
        if (nameEl) nameEl.textContent = '';
        if (dedication) dedication.classList.remove(VISIBLE_CLASS);
        writeSilence('');
        writeDuaa('');
      }

      if (hasName !== lastHasName) {
        lastHasName = hasName;
        publishSafely(PUBSUB_EVENT, { hasName: hasName });
      }
    }

    // Read-only listener: passive observation of IO's own name input only.
    nameInput.addEventListener('input', sync);

    // Sync once on bind in case of back-navigation/bfcache-restored values.
    sync();
  }

  function tryBindNow(buyContainer, dedication, silenceTwo, duaaEl) {
    var nameInput = buyContainer.querySelector(NAME_INPUT_SELECTOR);
    if (!nameInput) return false;
    bindFields(nameInput, dedication, silenceTwo, duaaEl);
    return true;
  }

  function waitForFieldsThenBind(buyContainer, dedication, silenceTwo, duaaEl) {
    var ioBlock = buyContainer.querySelector(IO_APP_BLOCK_SELECTOR);
    if (!ioBlock) return; // no IO app block on this product at all — fail silent

    var observer = new MutationObserver(function () {
      if (tryBindNow(buyContainer, dedication, silenceTwo, duaaEl)) {
        observer.disconnect();
        clearTimeout(giveUpTimer);
      }
    });
    observer.observe(ioBlock, { childList: true, subtree: true });

    var giveUpTimer = setTimeout(function () {
      observer.disconnect();
    }, GIVE_UP_MS);
  }

  function init(root) {
    var scope = root || document;
    var buyContainer = scope.querySelector(BUY_CONTAINER_SELECTOR) || scope;
    var dedication = buyContainer.querySelector(DEDICATION_SELECTOR) || scope.querySelector(DEDICATION_SELECTOR);
    var duaaEl = buyContainer.querySelector(DUAA_NAME_SELECTOR) || scope.querySelector(DUAA_NAME_SELECTOR);
    if (!dedication && !duaaEl) return;
    var silenceTwo = buyContainer.querySelector(SILENCE_TWO_SELECTOR) || scope.querySelector(SILENCE_TWO_SELECTOR);

    // IO's fields may already be mounted (e.g. bfcache restore) or may not
    // exist yet (typical first paint, app block loads asynchronously).
    if (!tryBindNow(buyContainer, dedication, silenceTwo, duaaEl)) {
      waitForFieldsThenBind(buyContainer, dedication, silenceTwo, duaaEl);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Exposed for re-init (if the beneficiary step is swapped via variant
  // change) and for QA-harness isolated testing.
  window.MRDedication = { init: init };
})();
